import { FormikProps } from 'formik'
import { AttendanceCustomTimeRangeDayEnum } from 'generated/resources/datatable.graphql'
import moment from 'moment'

import { useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateCustomTimeRange } from 'modules/attendance/hooks/useCreateCustomTimeRange'

import { CustomTimeRangesFormData } from './utils/CustomTimeRangesFormData'
import useHandleReumbursableMappings from './utils/useHandleReimbursableMappings'

interface Props {
  settingId: string
  dayType?: AttendanceCustomTimeRangeDayEnum
}

export const hasStartTime = (form: FormikProps<CustomTimeRangesFormData>) =>
  !!form.values.start_time
export const hasEndTime = (form: FormikProps<CustomTimeRangesFormData>) => !!form.values.end_time
const isFullDay = (form: FormikProps<CustomTimeRangesFormData>) =>
  !form.values.start_time && !form.values.end_time
export const isPayrolCompensation = (form: FormikProps<CustomTimeRangesFormData>) =>
  form.values.pool_type === 'virtual_payroll'

const createCustomTimeRanges = (data: CustomTimeRangesFormData) => {
  const defaultDate = '2000-01-01'
  const start_at = moment.utc(`${defaultDate} ${data.start_time}`).toISOString()
  const end_at = moment.utc(`${defaultDate} ${data.end_time}`).toISOString()

  return data.day.map((selectedDay) => ({
    attendanceCustomTimeRangeCategoryId: data.attendance_custom_time_range_category_id,
    poolType: data.pool_type ?? 'balance',
    day: selectedDay,
    startTime: start_at,
    endTime: end_at,
    timeSettingsCustomTimeRangeCategoryId: data.time_settings_custom_time_range_category_id,
    attendanceEmployeesSettingId: data.attendance_employees_setting_id,
  }))
}

const CreateCustomTimeRangesForm = ({ settingId, dayType }: Props) => {
  const { create } = useCreateCustomTimeRange()
  const { manageMappings } = useHandleReumbursableMappings()

  const defaultValues: CustomTimeRangesFormData = {
    attendance_custom_time_range_category_id: '',
    day: dayType ? [dayType] : [],
    pool_type: undefined,
    start_time: undefined,
    end_time: undefined,
    attendance_employees_setting_id: settingId.toString(),
    reimbursable_taxonomies_mappings: [],
    time_settings_custom_time_range_category_id: '',
  }

  const form: FormikProps<CustomTimeRangesFormData> = useForm<CustomTimeRangesFormData>({
    defaultValues: defaultValues,
    onSubmit: async (data, { setErrors }) => {
      const customTimeRanges = createCustomTimeRanges(data)
      const payload = { attendanceEmployeesSettingId: settingId, customTimeRanges }
      const result = await create(payload)

      const handled = handleResult(result, ({ day }) => {
        setErrors({ day })
      })

      if (handled.success) {
        const mappings = data.reimbursable_taxonomies_mappings || []

        manageMappings(setErrors, mappings)

        track(trackingEvents.timeTracking.addTimeBlock, {
          day: form.values.day,
          category_id: handled.data.customTimeRanges?.[0]?.timeSettingsCustomTimeRangeCategoryId,
          name: handled.data.customTimeRanges?.[0]?.name,
          time_block_type: isFullDay(form) ? 'full_day' : 'time_slot',
          start_time: form.values.start_time,
          end_time: form.values.end_time,
          pool_type: form.values.pool_type,
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      form.resetForm()
      return handled
    },
  })

  return form
}

export default CreateCustomTimeRangesForm
